import { useCallback, useEffect, useRef, useState } from "react";

import type { AnyChatClientFrame, AnyChatServerFrame } from "./types";

// Generic extraction of the consumer advisor socket hook (plan 188 W2-M8,
// task 801) — reused AS-IS behaviorally by both the consumer advisor dock
// (`frontend/consumer/src/hooks/useAdvisorSocket.ts`, a thin wrapper around
// this) and the Pro CRM Copilot dock (M8b). The connection lifecycle,
// pending-frame queue, and reconnect-on-send behavior are unchanged from the
// original consumer-only implementation; only the server-frame handling
// callback and the default WS path are now parameters instead of hard-wired
// to the consumer's `useAdvisorStore` / `/ws/agent/chat`.

export type ChatSocketStatus = "connecting" | "open" | "closed";

export type UseChatSocket<TClientFrame> = {
  status: ChatSocketStatus;
  send: (frame: TClientFrame) => void;
};

export type UseChatSocketOptions<TServerFrame> = {
  /**
   * Async token resolver, called fresh for EVERY connection attempt. Clerk
   * session JWTs expire in ~60s, and Cloud Run severs long-lived sockets at
   * its request timeout — a reconnect that replays a token captured at mount
   * is guaranteed to fail the `auth_token` handshake once the first
   * connection outlives the token. Resolving at connect time keeps the
   * handshake on the same always-fresh path as REST calls. Identity may be
   * unstable across renders; the hook reads it through a ref.
   */
  getToken: () => Promise<string | null>;
  /** When false, no connection is attempted and status reports "closed". */
  enabled?: boolean;
  url?: string;
  /** WS path used when `url` is not explicitly provided, e.g. "/ws/agent/chat" or "/ws/professional/chat". */
  defaultPath: string;
  onServerFrame: (frame: TServerFrame) => void;
  /**
   * Called when the socket closes for a reason OTHER than our own teardown
   * (unmount, deps change, or a reconnect triggered by send()). A server- or
   * network-initiated close — including the case where the backend rejected the
   * handshake but its error frame was lost, or the connection dropped mid-turn —
   * fires this so the caller can clear a pending "thinking" state instead of
   * spinning forever. Not called for intentional client-side closes.
   */
  onUnexpectedClose?: () => void;
};

function resolveUrl(defaultPath: string, override?: string): string {
  if (override) return override;
  const envUrl = (import.meta.env.VITE_WS_URL as string | undefined) ?? "";
  return `${envUrl}${defaultPath}`;
}

export function useChatSocket<
  TServerFrame extends AnyChatServerFrame,
  TClientFrame extends AnyChatClientFrame,
>({
  getToken,
  enabled = true,
  url,
  defaultPath,
  onServerFrame,
  onUnexpectedClose,
}: UseChatSocketOptions<TServerFrame>): UseChatSocket<TClientFrame> {
  const [status, setStatus] = useState<ChatSocketStatus>("connecting");
  const [connectionAttempt, setConnectionAttempt] = useState(0);
  const socketRef = useRef<WebSocket | null>(null);
  const pendingFramesRef = useRef<TClientFrame[]>([]);
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  // Read through a ref so the effect below doesn't re-subscribe when the caller
  // passes a fresh closure each render (mirrors getTokenRef).
  const onUnexpectedCloseRef = useRef(onUnexpectedClose);
  onUnexpectedCloseRef.current = onUnexpectedClose;

  const flushPendingFrames = useCallback((ws: WebSocket) => {
    while (
      ws.readyState === WebSocket.OPEN &&
      pendingFramesRef.current.length > 0
    ) {
      const frame = pendingFramesRef.current.shift();
      if (frame) ws.send(JSON.stringify(frame));
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const resolved = resolveUrl(defaultPath, url);

    let cancelled = false;
    // Set true right before WE close the socket (unmount / deps change /
    // reconnect). A close observed while this is still false was initiated by
    // the server or network, which is what onUnexpectedClose reports.
    let intentionalClose = false;
    let teardown = () => {};

    void (async () => {
      const token = await getTokenRef.current();
      if (cancelled) return;
      if (!token) {
        // Unauthenticated (e.g. Clerk still booting): report closed. A later
        // send() queues the frame and bumps connectionAttempt, which re-runs
        // this effect and re-resolves the token.
        setStatus("closed");
        return;
      }

      const ws = new WebSocket(resolved);
      socketRef.current = ws;

      const onOpen = () => {
        ws.send(JSON.stringify({ type: "auth_token", token }));
        flushPendingFrames(ws);
        setStatus("open");
      };
      const onClose = () => {
        socketRef.current = null;
        setStatus("closed");
        if (!intentionalClose) {
          onUnexpectedCloseRef.current?.();
        }
      };
      const onMessage = (event: MessageEvent) => {
        try {
          const frame = JSON.parse(event.data as string) as TServerFrame;
          onServerFrame(frame);
        } catch {
          onServerFrame({
            type: "error",
            code: "malformed_frame",
            error: "Received a frame that could not be parsed as JSON.",
          } as unknown as TServerFrame);
        }
      };

      ws.addEventListener("open", onOpen);
      ws.addEventListener("close", onClose);
      ws.addEventListener(
        "message",
        onMessage as unknown as EventListenerOrEventListenerObject,
      );

      teardown = () => {
        intentionalClose = true;
        ws.removeEventListener("open", onOpen);
        ws.removeEventListener("close", onClose);
        ws.removeEventListener(
          "message",
          onMessage as unknown as EventListenerOrEventListenerObject,
        );
        ws.close();
        socketRef.current = null;
      };
    })();

    return () => {
      cancelled = true;
      teardown();
    };
  }, [
    enabled,
    url,
    defaultPath,
    connectionAttempt,
    flushPendingFrames,
    onServerFrame,
  ]);

  const send = useCallback(
    (frame: TClientFrame) => {
      const ws = socketRef.current;
      if (
        !ws ||
        ws.readyState === WebSocket.CLOSED ||
        ws.readyState === WebSocket.CLOSING
      ) {
        pendingFramesRef.current.push(frame);
        if (enabled) {
          setStatus("connecting");
          setConnectionAttempt((attempt) => attempt + 1);
        }
        return;
      }
      if (ws.readyState !== WebSocket.OPEN) {
        pendingFramesRef.current.push(frame);
        return;
      }
      ws.send(JSON.stringify(frame));
    },
    [enabled],
  );

  return { status: enabled ? status : "closed", send };
}
